import { ContactFormData } from '@upright/common/activities/contact-forms/facade/contact-form';
import { friendlySsoProvider } from '@upright/common/iam/login';
import { SsoErrorPayload, SsoErrorType, SsoProvider } from '@upright/common/iam/login/types';
import { removeTokenFromStorageAndForceNewSsoLogin } from '@upright/ui/api/iam/sso';
import { ContactModalLink } from '@upright/ui/common/activities/contact-and-feedback/contact-modal-link';
import { Text } from '@upright/ui/common/generic/components/primer-custom/text';
import { BouSpinner, BouSpinnerSize } from '@upright/ui/common/generic/components/progress/spinners/bou-spinner';
import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useQueryParams } from '@upright/ui/common/generic/hooks/url-handling/query-params';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ErrorWithIcon } from '../../error-view/error-with-icon';

export const SsoErrorView = () => {
  const [params, setParams] = useQueryParams<{ error: string | undefined }>(['error']);
  const [error, setError] = useState<SsoErrorPayload>();

  useEffect(() => {
    if (params.error) {
      setError(JSON.parse(params.error) as SsoErrorPayload);
      setParams({ error: undefined });
    }
  }, [params.error, setParams]);

  if (!error) {
    return <></>;
  }

  if (error.type === SsoErrorType.NEW_LOGIN_REQUIRED) {
    return (
      <ForwardingToLoginAgain ssoProvider={error.ssoProvider} afterSuccessRedirectUrl={error.afterSuccessRedirectUrl} />
    );
  }

  return (
    <Root>
      <ErrorWithIcon
        title={'Single sign-on failed'}
        content={
          <ErrorContentRoot>
            <div>
              <ErrorText error={error} />
            </div>
            {error.ssoLoginAttemptId && (
              <div>
                <IdText>Login attempt id: {error.ssoLoginAttemptId}</IdText>
              </div>
            )}
          </ErrorContentRoot>
        }
      />
    </Root>
  );
};

const Root = styled.div``;

const ErrorContentRoot = styled.div`
  > div + div {
    margin-top: 1.5em;
  }
  word-break: break-word;
`;

const IdText = styled.span`
  ${getTextStyle({ size: 'xxxs' })}
  color: ${BouColor.PLATFORM_TEXT_GRAY};
`;

const ErrorText = (props: { error: SsoErrorPayload }) => {
  const contactFormInitialValues: Partial<ContactFormData> = {
    debugData: props.error,
  };

  const tryAgainText = (
    <>
      Please try again, and if the problem persists, please{' '}
      <ContactModalLink initialValues={contactFormInitialValues}>contact us</ContactModalLink>
    </>
  );

  switch (props.error.type) {
    case SsoErrorType.OPEN_ID: {
      return <>There was a problem with the response received from the single-sign-on provider. {tryAgainText}.</>;
    }
    case SsoErrorType.LOGIN_ATTEMPT_EXPIRED: {
      return <>The login attempt has expired. Please try again.</>;
    }
    case SsoErrorType.ORGANIZATION_MISMATCH: {
      return (
        <>
          Your single sign-on organization is different than the one linked to your organization on the Upright
          Platform.
          <ContactModalLink initialValues={contactFormInitialValues}>please contact us</ContactModalLink>.
        </>
      );
    }
    case SsoErrorType.EMAIL_MISSING_ON_USER_CREATION: {
      return <>No email address received from {props.error.ssoProvider}, cannot sign up.</>;
    }
    case SsoErrorType.EMAIL_NOT_VERIFIED: {
      return (
        <>
          You already have an Upright Platform account but you have not verified your email address. We cannot let you
          log in with {props.error.ssoProvider} before you have completed the verification. Please click the
          verification link that you have received in email when you created your user account. If you cannot find the
          email, <ContactModalLink initialValues={contactFormInitialValues}>please contact us</ContactModalLink>.
        </>
      );
    }
    case SsoErrorType.USER_NOT_FOUND: {
      return (
        <>
          No matching Upight Platform user found. Email {props.error.email}, SSO user id {props.error.ssoServiceUserId}.
        </>
      );
    }
    case SsoErrorType.NEW_LOGIN_REQUIRED: {
      return <>Please log in again</>;
    }
    case SsoErrorType.OTHER: {
      return (
        <>
          {props.error.message}
          <br /> <br />
          {tryAgainText}.
        </>
      );
    }
  }
};

const ForwardingToLoginAgain = (props: { ssoProvider: SsoProvider; afterSuccessRedirectUrl: string }) => {
  void removeTokenFromStorageAndForceNewSsoLogin(props.ssoProvider, props.afterSuccessRedirectUrl);
  return (
    <NewLoginForwardRoot>
      <div>
        <Text fontSize={'large'}>New login required, redirecting to {friendlySsoProvider(props.ssoProvider)}.</Text>
        <BouSpinner size={BouSpinnerSize.SMALL} />
      </div>
    </NewLoginForwardRoot>
  );
};

const NewLoginForwardRoot = styled.div`
  margin-left: auto;
  margin-right: auto;
  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: ${themeGet('space.3')};
    > * + * {
      margin-top: ${themeGet('space.3')};
    }
  }
`;
